<template>
  <div class="klv3-container">
    <div v-if="isLoading" class="state-block" data-state="loading">
      <v-progress-circular indeterminate color="#EA5545" size="64"></v-progress-circular>
      <p class="state-text">{{ t('klv3.loading') }}</p>
    </div>

    <div v-else-if="klarvertragData.length > 0" class="klv3-questions">
      <v-card
        v-for="(item, index) in klarvertragData"
        :key="item.frage_id || index"
        class="klv3-question-card"
        elevation="1"
      >
        <v-card-text>
          <div class="klv3-question-header">
            <v-icon size="20" color="#EA5545" class="mr-2">mdi-help-circle</v-icon>
            <h3 class="klv3-question-title">{{ item.frage_text }}</h3>
          </div>
          <div class="klv3-answer">
            <v-icon size="18" color="#4caf50" class="mr-2">mdi-check-circle</v-icon>
            <div class="klv3-answer-text" v-html="item.antwort_text"></div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <div v-else class="state-block" data-state="empty">
      <v-icon size="80" color="#ccc">mdi-file-document-outline</v-icon>
      <h3 class="state-title">{{ t('klv3.emptyTitle') }}</h3>
      <p class="state-text">
        {{ t('klv3.emptyDescription') }}
      </p>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, inject, onMounted, watch } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

const { t } = useLanguage()

const props = defineProps({
  product: {
    type: Object,
    default: null
  },
  tarifId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['product-loaded'])

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')

const klarvertragData = ref([])
const isLoading = ref(false)

async function loadKlarvertrag() {
  if (!props.tarifId) {
    klarvertragData.value = []
    return
  }

  isLoading.value = true
  try {
    const payload = {
      action: 'getklarvertrag',
      tarif_id: Number(props.tarifId)
    }

    if (props.product?.myp_id) {
      payload.myp_id = props.product.myp_id
    }

    const response = await baseCalls.clearmentPost(payload)

    if (response.data.success && response.data.klarvertrag) {
      klarvertragData.value = response.data.klarvertrag
      emit('product-loaded', response.data.product || props.product)
    } else {
      klarvertragData.value = []
      if (response.data.message) {
        errorStore.setError(true, response.data.message)
      }
    }
  } catch (err) {
    console.error('Error loading Klarvertrag Type 3:', err)
    errorStore.setError(true, 'Fehler beim Laden des Klarvertrags')
    klarvertragData.value = []
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.tarifId,
  () => {
    loadKlarvertrag()
  },
  { immediate: false }
)

onMounted(() => {
  loadKlarvertrag()
})
</script>

<style scoped>
.klv3-container {
  margin-top: 24px;
}

.klv3-questions {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.klv3-question-card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  transition: box-shadow 0.2s ease;
}

.klv3-question-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.klv3-question-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.klv3-question-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.4;
}

.klv3-answer {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background: #f0fdf4;
  border-left: 3px solid #4caf50;
  border-radius: 6px;
}

.klv3-answer-text {
  margin: 0;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  flex: 1;
}

.klv3-answer-text :deep(p) {
  margin: 0;
  padding: 0;
}

.klv3-answer-text :deep(p:not(:last-child)) {
  margin-bottom: 8px;
}

.klv3-answer-text :deep(ul),
.klv3-answer-text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.klv3-answer-text :deep(li) {
  margin: 4px 0;
}

.state-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.state-block[data-state="loading"] {
  gap: 12px;
}

.state-block[data-state="empty"] {
  gap: 16px;
}

.state-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.state-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  max-width: 500px;
}
</style>

